// screens/ReportScreen.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  RefreshControl,
  TouchableOpacity,
  ActivityIndicator,
  Modal,
  TextInput,
  FlatList,
  Pressable,
  Platform,
} from 'react-native';
import axios from 'axios';
import { API_BASE } from '../utils';
import EnterpriseHeader from '../components/EnterpriseHeader';
import PadiProIcon from '../components/PadiProIcon';
import { PADIPRO, padiproShadow } from '../components/padiproTheme';
import { useResponsive } from '../components/responsive';

// ✅ Victory Native (legacy-compatible)
import {
  VictoryAxis,
  VictoryBar,
  VictoryChart,
  VictoryLine,
  VictoryTheme,
  VictoryLegend,
  VictoryArea,
} from 'victory-native';

const UI = {
  bg: PADIPRO.bg,
  card: PADIPRO.surface,
  border: PADIPRO.border,
  text: PADIPRO.text,
  sub: PADIPRO.muted,
  faint: '#80937f',
  shadow: '#10291b',

  income: '#15803D',
  incomeSoft: '#DCFCE7',

  expense: '#B45309',
  expenseSoft: '#FEF3C7',

  net: '#0F766E',
  netSoft: '#CCFBF1',

  axis: '#789081',
  grid: '#E6EEE8',

  bar: PADIPRO.primary,
  barSoft: PADIPRO.primarySoft,

  dangerBg: PADIPRO.dangerSoft,
  dangerBorder: 'rgba(159, 33, 56, 0.25)',
  dangerText: PADIPRO.danger,
};

const formatIDR = (n) => {
  const v = Number(n || 0);
  return 'Rp ' + v.toLocaleString('id-ID');
};

const safeNum = (n) => {
  const x = Number(n);
  return Number.isFinite(x) ? x : 0;
};

const pct = (x) => {
  const v = safeNum(x) * 100;
  if (!Number.isFinite(v)) return '0%';
  return `${v.toFixed(v < 10 ? 1 : 0)}%`;
};

function compactAxis(v) {
  const n = safeNum(v);
  const a = Math.abs(n);
  if (a >= 1e9) return `${Math.round(n / 1e9)} M`;
  if (a >= 1e6) return `${Math.round(n / 1e6)} jt`;
  if (a >= 1e3) return `${Math.round(n / 1e3)} rb`;
  return `${Math.round(n)}`;
}

function shortDate(s) {
  if (!s) return '';
  return String(s).slice(0, 10);
}

function monthLabel(period) {
  // "YYYY-MM" -> "MM"
  const s = String(period || '');
  return s ? s.slice(5, 7) : '';
}

export default function ReportScreen({ route }) {
  const { token } = route.params || {};
  const { contentWidthStyle, maxWidth, width } = useResponsive();

  const authHdr = useMemo(
    () => (token ? { Authorization: `Bearer ${token}` } : {}),
    [token]
  );

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [seasons, setSeasons] = useState([]);
  const [seasonId, setSeasonId] = useState(null);
  const [seasonModalOpen, setSeasonModalOpen] = useState(false);

  const [report, setReport] = useState(null);
  const [err, setErr] = useState(null);

  const loadSeasons = useCallback(async () => {
    const s = await axios.get(`${API_BASE}/seasons`, { headers: authHdr });
    const list = Array.isArray(s.data) ? s.data : [];
    const active = list.find((x) => x && x.end_date == null) || null;

    setSeasons(list);
    setSeasonId((prev) => prev || active?.season_id || list?.[0]?.season_id || null);
    return list;
  }, [authHdr]);

  const loadReport = useCallback(
    async (sid) => {
      if (!sid) {
        setReport(null);
        return;
      }

      const r = await axios.get(`${API_BASE}/reports/season-finance`, {
        headers: authHdr,
        params: { season_id: sid },
      });

      setReport(r.data || null);
    },
    [authHdr]
  );

  const loadAll = useCallback(
    async (opts = { silent: false }) => {
      const silent = !!opts.silent;
      if (!silent) setLoading(true);
      setErr(null);

      try {
        const list = await loadSeasons();
        const active = list.find((x) => x && x.end_date == null) || null;
        const sid = seasonId || active?.season_id || list?.[0]?.season_id || null;

        if (sid) {
          setSeasonId(sid);
          await loadReport(sid);
        } else {
          setReport(null);
        }
      } catch (e) {
        setErr(e?.response?.data?.error || e?.message || 'Failed to load report');
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [loadSeasons, loadReport, seasonId]
  );

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!seasonId) return;
    (async () => {
      try {
        setErr(null);
        await loadReport(seasonId);
      } catch (e) {
        setErr(e?.response?.data?.error || e?.message || 'Failed to load report');
      }
    })();
  }, [seasonId, loadReport]);

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await loadAll({ silent: true });
    } finally {
      setRefreshing(false);
    }
  }, [loadAll]);

  const activeSeason = useMemo(() => {
    if (!seasonId) return null;
    return seasons.find((s) => String(s.season_id) === String(seasonId)) || null;
  }, [seasons, seasonId]);

  const kpi = report?.kpi || {};
  const series = Array.isArray(report?.series_monthly) ? report.series_monthly : [];
  const byCat = Array.isArray(report?.expense_breakdown?.by_category)
    ? report.expense_breakdown.by_category
    : [];
  const byField = Array.isArray(report?.income_breakdown?.by_field)
    ? report.income_breakdown.by_field
    : [];
  const varietyHst = Array.isArray(report?.variety_harvest_analysis)
    ? report.variety_harvest_analysis
    : [];

  const layoutWidth = maxWidth ? Math.min(width || maxWidth, maxWidth) : width;
  const chartWidth = Math.max(280, Math.min((layoutWidth || 360) - 32, 760));

  const normalizedSeries = useMemo(() => {
    return series.map((x) => ({
      period: monthLabel(x.period),
      income: safeNum(x.income),
      expense: safeNum(x.expense),
      net: safeNum(x.net),
    }));
  }, [series]);

  const topCats = useMemo(() => {
    return byCat
      .slice(0, 8)
      .map((x) => ({
        key: String(x.key || ''),
        total: safeNum(x.total),
      }))
      .reverse();
  }, [byCat]);

  const headerSubtitle = useMemo(() => {
    if (!activeSeason) return '—';
    const pieces = [];
    if (activeSeason?.name) pieces.push(activeSeason.name);
    if (activeSeason?.start_date) pieces.push(`start ${shortDate(activeSeason.start_date)}`);
    if (activeSeason?.end_date) pieces.push(`end ${shortDate(activeSeason.end_date)}`);
    else pieces.push('active');
    return pieces.join(' • ');
  }, [activeSeason]);

  return (
    <View style={styles.screen}>
      <ScrollView
        style={styles.container}
        contentContainerStyle={[styles.content, contentWidthStyle]}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={PADIPRO.primary} />}
        keyboardShouldPersistTaps="handled"
        contentInsetAdjustmentBehavior="automatic"
        showsVerticalScrollIndicator={false}
      >
        <View style={styles.heroWrap}>
          <EnterpriseHeader
            title="Laporan Finansial"
            subtitle={headerSubtitle}
            right={(
              <TouchableOpacity
                style={styles.heroIconBtn}
                onPress={onRefresh}
                activeOpacity={0.85}
                hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
              >
                <PadiProIcon name="report" size={16} color="#f4fff8" strokeWidth={2} />
              </TouchableOpacity>
            )}
          />

          <View style={styles.heroActions}>
            <TouchableOpacity style={styles.heroActionBtn} onPress={() => setSeasonModalOpen(true)} activeOpacity={0.9}>
              <PadiProIcon name="harvest" size={15} color={UI.text} strokeWidth={2} />
              <Text style={styles.heroActionText}>Pilih Musim</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.heroActionBtn} onPress={onRefresh} activeOpacity={0.9}>
              <PadiProIcon name="operations" size={15} color={UI.text} strokeWidth={2} />
              <Text style={styles.heroActionText}>Refresh</Text>
            </TouchableOpacity>
          </View>
        </View>

        {/* Loading / Error / Empty */}
        {loading && (
          <View style={styles.center}>
            <ActivityIndicator size="large" />
            <Text style={styles.muted}>Loading report…</Text>
          </View>
        )}

        {!!err && !loading && (
          <View style={styles.errorBox}>
            <Text style={styles.errorTitle}>Failed to load</Text>
            <Text style={styles.errorText}>{String(err)}</Text>

            <TouchableOpacity style={[styles.btn, { alignSelf: 'flex-start', marginTop: 10 }]} onPress={() => loadAll()}>
              <Text style={styles.btnText}>Retry</Text>
            </TouchableOpacity>
          </View>
        )}

        {!loading && !err && !report && (
          <View style={styles.emptyBox}>
            <Text style={styles.emptyTitle}>No data</Text>
            <Text style={styles.muted}>Season belum dipilih atau belum ada transaksi/panen.</Text>
          </View>
        )}

        {/* Content */}
        {!loading && !err && !!report && (
          <>
            {/* KPI cards */}
            <View style={styles.kpiGrid}>
              <KpiCard
                title="Total Income"
                value={formatIDR(kpi.total_income)}
                sub="Panen (kg × harga)"
                tone="income"
              />
              <KpiCard
                title="Total Expense"
                value={formatIDR(kpi.total_expense)}
                sub="Semua biaya season"
                tone="expense"
              />
              <KpiCard
                title="Net Profit"
                value={formatIDR(kpi.net_profit)}
                sub={`Margin ${pct(kpi.margin)}`}
                tone="net"
              />
              <KpiCard
                title="Cost / kg"
                value={formatIDR(kpi.cost_per_kg)}
                sub={`Avg price ${formatIDR(kpi.avg_price)}`}
                tone="neutral"
              />
            </View>

            {/* Cashflow */}
            <View style={styles.card}>
              <View style={styles.cardHeadRow}>
                <View style={{ flex: 1, paddingRight: 10 }}>
                  <Text style={styles.cardTitle}>Cashflow</Text>
                  <Text style={styles.cardSub}>Income, expense, net (monthly)</Text>
                </View>
              </View>

              {normalizedSeries.length === 0 ? (
                <Text style={styles.muted}>Belum ada data bulanan untuk season ini.</Text>
              ) : (
                <View style={{ width: chartWidth }}>
                  <VictoryChart
                    theme={VictoryTheme.material}
                    height={300}
                    padding={{ top: 28, bottom: 50, left: 78, right: 16 }}
                    domainPadding={{ x: 12, y: 10 }}
                  >
                    <VictoryLegend
                      x={44}
                      y={6}
                      orientation="horizontal"
                      gutter={16}
                      style={{ labels: { fill: UI.sub, fontSize: 11 } }}
                      data={[
                        { name: 'Income', symbol: { fill: UI.income } },
                        { name: 'Expense', symbol: { fill: UI.expense } },
                        { name: 'Net', symbol: { fill: UI.net } },
                      ]}
                    />

                    <VictoryAxis
                      style={{
                        axis: { stroke: UI.grid },
                        tickLabels: { fill: UI.axis, fontSize: 11, padding: 6 },
                        grid: { stroke: 'transparent' },
                      }}
                    />
                    <VictoryAxis
                      dependentAxis
                      tickFormat={compactAxis}
                      style={{
                        axis: { stroke: UI.grid },
                        tickLabels: { fill: UI.axis, fontSize: 11, padding: 6 },
                        grid: { stroke: UI.grid },
                      }}
                    />

                    {/* Income */}
                    <VictoryArea
                      data={normalizedSeries}
                      x="period"
                      y="income"
                      style={{ data: { fill: UI.incomeSoft, opacity: 0.55 } }}
                    />
                    <VictoryLine
                      data={normalizedSeries}
                      x="period"
                      y="income"
                      style={{ data: { stroke: UI.income, strokeWidth: 2.5 } }}
                    />

                    {/* Expense */}
                    <VictoryArea
                      data={normalizedSeries}
                      x="period"
                      y="expense"
                      style={{ data: { fill: UI.expenseSoft, opacity: 0.45 } }}
                    />
                    <VictoryLine
                      data={normalizedSeries}
                      x="period"
                      y="expense"
                      style={{ data: { stroke: UI.expense, strokeWidth: 2.5 } }}
                    />

                    {/* Net */}
                    <VictoryArea
                      data={normalizedSeries}
                      x="period"
                      y="net"
                      style={{ data: { fill: UI.netSoft, opacity: 0.4 } }}
                    />
                    <VictoryLine
                      data={normalizedSeries}
                      x="period"
                      y="net"
                      style={{ data: { stroke: UI.net, strokeWidth: 2.5 } }}
                    />
                  </VictoryChart>
                </View>
              )}
            </View>

            {/* Expense breakdown */}
            <View style={styles.card}>
              <View style={styles.cardHeadRow}>
                <View style={{ flex: 1, paddingRight: 10 }}>
                  <Text style={styles.cardTitle}>Expense Breakdown</Text>
                  <Text style={styles.cardSub}>Top categories (season total)</Text>
                </View>
              </View>

              {topCats.length === 0 ? (
                <Text style={styles.muted}>Belum ada expense pada season ini.</Text>
              ) : (
                <View style={{ width: chartWidth }}>
                  <VictoryChart
                    theme={VictoryTheme.material}
                    height={340}
                    padding={{ top: 18, bottom: 90, left: 78, right: 16 }}
                    domainPadding={{ x: 18, y: 10 }}
                  >
                    <VictoryAxis
                      tickFormat={(t) => String(t).slice(0, 10)}
                      style={{
                        axis: { stroke: UI.grid },
                        tickLabels: { angle: -35, fontSize: 10, fill: UI.axis, padding: 18 },
                        grid: { stroke: 'transparent' },
                      }}
                    />
                    <VictoryAxis
                      dependentAxis
                      tickFormat={compactAxis}
                      style={{
                        axis: { stroke: UI.grid },
                        tickLabels: { fill: UI.axis, fontSize: 11, padding: 6 },
                        grid: { stroke: UI.grid },
                      }}
                    />

                    <VictoryBar
                      data={topCats}
                      x="key"
                      y="total"
                      barRatio={0.7}
                      cornerRadius={{ top: 8, bottom: 8 }}
                      style={{
                        data: { fill: UI.bar, opacity: 0.85 },
                      }}
                    />
                  </VictoryChart>
                </View>
              )}

              {/* Friendly list */}
              {byCat.slice(0, 8).map((x, idx) => (
                <BreakdownRow
                  key={`${x.key}-${idx}`}
                  label={String(x.key || '-')}
                  value={formatIDR(x.total)}
                  amount={safeNum(x.total)}
                  max={Math.max(...byCat.slice(0, 8).map((i) => safeNum(i.total)), 1)}
                />
              ))}
            </View>

            {/* Income by field */}
            <View style={styles.card}>
              <View style={styles.cardHeadRow}>
                <View style={{ flex: 1, paddingRight: 10 }}>
                  <Text style={styles.cardTitle}>Hasil &amp; Pendapatan per Lahan</Text>
                  <Text style={styles.cardSub}>Produktivitas dihitung dari total kg ÷ luas parent lahan.</Text>
                </View>
              </View>

              {byField.length === 0 ? (
                <Text style={styles.muted}>Belum ada panen pada season ini.</Text>
              ) : (
                byField.map((x, idx) => (
                  <View key={`${x.field_id}-${idx}`} style={styles.rowBetween}>
                    <View style={{ flex: 1, paddingRight: 10 }}>
                      <Text style={styles.rowKey}>{x.field_name || `Lahan #${x.field_id}`}</Text>
                      <Text style={styles.mutedSmall}>
                        {safeNum(x.total_kg).toLocaleString('id-ID')} kg
                        {safeNum(x.area_rante) > 0
                          ? ` • ${safeNum(x.area_rante).toLocaleString('id-ID')} rante • ${safeNum(x.kg_per_rante).toLocaleString('id-ID', { maximumFractionDigits: 2 })} kg/rante`
                          : ' • luas rante belum dicatat'}
                      </Text>
                    </View>
                    <Text style={styles.rowVal}>{formatIDR(x.total_income)}</Text>
                  </View>
                ))
              )}
            </View>

            {/* Actual harvest age by rice variety */}
            <View style={styles.card}>
              <View style={styles.cardHeadRow}>
                <View style={{ flex: 1, paddingRight: 10 }}>
                  <Text style={styles.cardTitle}>Umur Panen Aktual per Varietas</Text>
                  <Text style={styles.cardSub}>HST dihitung dari tanggal tanam hingga hasil panen pertama.</Text>
                </View>
              </View>

              {varietyHst.length === 0 ? (
                <Text style={styles.muted}>Belum cukup data tanggal tanam dan hasil panen untuk musim ini.</Text>
              ) : (
                varietyHst.map((x, idx) => (
                  <View key={`${x.variety}-${idx}`} style={styles.varietyRow}>
                    <View style={{ flex: 1, paddingRight: 12 }}>
                      <Text style={styles.rowKey}>{x.variety || 'Varietas belum dicatat'}</Text>
                      <Text style={styles.varietyFieldText}>
                        Lahan: {Array.isArray(x.field_names) && x.field_names.length ? x.field_names.join(', ') : 'Belum dicatat'}
                      </Text>
                      <Text style={styles.mutedSmall}>
                        {safeNum(x.cycle_count).toLocaleString('id-ID')} siklus • {safeNum(x.total_kg).toLocaleString('id-ID')} kg
                      </Text>
                      <Text style={styles.varietyDateText}>
                        Data panen {shortDate(x.first_harvest_date) || '—'} – {shortDate(x.last_harvest_date) || '—'}
                      </Text>
                    </View>
                    <View style={styles.hstBox}>
                      <Text style={styles.hstValue}>{safeNum(x.avg_hst).toFixed(1)}</Text>
                      <Text style={styles.hstLabel}>rata-rata HST</Text>
                      <Text style={styles.hstRange}>{safeNum(x.min_hst)}–{safeNum(x.max_hst)} HST</Text>
                    </View>
                  </View>
                ))
              )}
            </View>

            {/* Footer spacing */}
            <View style={{ height: 12 }} />
          </>
        )}
      </ScrollView>

      {/* Season Picker Modal */}
      <SeasonPickerModal
        visible={seasonModalOpen}
        seasons={seasons}
        activeSeasonId={seasonId}
        onClose={() => setSeasonModalOpen(false)}
        onSelect={(sid) => {
          setSeasonId(sid);
          setSeasonModalOpen(false);
        }}
      />
    </View>
  );
}

function KpiCard({ title, value, sub, tone = 'neutral' }) {
  const stripeColor =
    tone === 'income' ? UI.income : tone === 'expense' ? UI.expense : tone === 'net' ? UI.net : UI.border;

  const chipBg =
    tone === 'income' ? UI.incomeSoft : tone === 'expense' ? UI.expenseSoft : tone === 'net' ? UI.netSoft : PADIPRO.surfaceSunken;

  return (
    <View style={styles.kpiCard}>
      <View style={[styles.kpiStripe, { backgroundColor: stripeColor }]} />

      <View style={styles.kpiTopRow}>
        <Text style={styles.kpiTitle}>{title}</Text>
        <View style={[styles.kpiChip, { backgroundColor: chipBg, borderColor: UI.border }]}>
          <Text style={styles.kpiChipText}>Season</Text>
        </View>
      </View>

      <Text style={styles.kpiValue}>{value}</Text>
      <Text style={styles.kpiSub}>{sub}</Text>
    </View>
  );
}

function BreakdownRow({ label, value, amount, max }) {
  const pct = max > 0 ? Math.min(1, amount / max) : 0;
  return (
    <View style={styles.breakRow}>
      <View style={{ flex: 1, paddingRight: 10 }}>
        <Text style={styles.rowKey}>{label}</Text>
        <View style={styles.barTrack}>
          <View style={[styles.barFill, { width: `${Math.round(pct * 100)}%` }]} />
        </View>
      </View>
      <Text style={styles.rowVal}>{value}</Text>
    </View>
  );
}

function SeasonPickerModal({ visible, seasons, activeSeasonId, onClose, onSelect }) {
  const [q, setQ] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (visible) {
      setQ('');
      setTimeout(() => inputRef.current?.focus?.(), Platform.OS === 'android' ? 350 : 150);
    }
  }, [visible]);

  const filtered = useMemo(() => {
    const s = String(q || '').trim().toLowerCase();
    if (!s) return seasons;
    return seasons.filter((x) => {
      const name = String(x?.name || '').toLowerCase();
      const id = String(x?.season_id || '').toLowerCase();
      return name.includes(s) || id.includes(s);
    });
  }, [q, seasons]);

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <Pressable style={styles.modalBackdrop} onPress={onClose}>
        <Pressable style={styles.modalCard} onPress={() => {}}>
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>Select season</Text>
            <TouchableOpacity onPress={onClose} style={styles.modalCloseBtn}>
              <Text style={styles.modalCloseText}>Close</Text>
            </TouchableOpacity>
          </View>

          <View style={styles.modalSearchWrap}>
            <TextInput
              ref={inputRef}
              value={q}
              onChangeText={setQ}
              placeholder="Search season..."
              placeholderTextColor={UI.faint}
              style={styles.modalSearch}
            />
          </View>

          <FlatList
            data={filtered}
            keyExtractor={(item) => String(item?.season_id)}
            keyboardShouldPersistTaps="handled"
            showsVerticalScrollIndicator={false}
            ItemSeparatorComponent={() => <View style={styles.modalSep} />}
            renderItem={({ item }) => {
              const sid = item?.season_id;
              const isActive = String(sid) === String(activeSeasonId);
              const right = item?.end_date == null ? 'Active' : shortDate(item?.end_date);

              return (
                <TouchableOpacity
                  onPress={() => onSelect(sid)}
                  style={[styles.modalRow, isActive ? styles.modalRowActive : null]}
                >
                  <View style={{ flex: 1, paddingRight: 10 }}>
                    <Text style={[styles.modalRowTitle, isActive ? { color: UI.text } : null]}>
                      {item?.name || String(sid).slice(0, 8)}
                    </Text>
                    <Text style={styles.modalRowSub}>
                      {item?.start_date ? shortDate(item.start_date) : '—'} • {right}
                    </Text>
                  </View>

                  {isActive ? (
                    <View style={styles.badgeActive}>
                      <Text style={styles.badgeActiveText}>Selected</Text>
                    </View>
                  ) : (
                    <View style={styles.badge}>
                      <Text style={styles.badgeText}>Pick</Text>
                    </View>
                  )}
                </TouchableOpacity>
              );
            }}
            ListEmptyComponent={
              <View style={{ paddingVertical: 16 }}>
                <Text style={styles.muted}>No season found.</Text>
              </View>
            }
            style={{ maxHeight: 440 }}
          />
        </Pressable>
      </Pressable>
    </Modal>
  );
}

const styles = StyleSheet.create({
  screen: { flex: 1, backgroundColor: UI.bg },
  container: { flex: 1, backgroundColor: UI.bg },
  content: { padding: 16, paddingBottom: 40 },

  heroWrap: {
    marginBottom: 12,
  },
  heroActions: {
    marginTop: 10,
    flexDirection: 'row',
    gap: 8,
    flexWrap: 'wrap',
  },
  heroActionBtn: {
    flexGrow: 1,
    minWidth: 140,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    backgroundColor: UI.card,
    paddingVertical: 10,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: UI.border,
    ...padiproShadow,
  },
  heroActionText: {
    color: UI.text,
    fontWeight: '800',
    fontSize: 13,
  },
  heroIconBtn: {
    width: 36,
    height: 36,
    borderRadius: 999,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,255,255,0.16)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.38)',
  },

  btn: {
    backgroundColor: UI.card,
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: UI.border,
    ...padiproShadow,
  },
  btnText: { color: UI.text, fontWeight: '800' },

  center: { paddingVertical: 40, alignItems: 'center' },
  muted: { color: UI.sub, marginTop: 6 },
  mutedSmall: { color: UI.sub, fontSize: 12, marginTop: 2 },

  errorBox: {
    backgroundColor: UI.dangerBg,
    borderColor: UI.dangerBorder,
    borderWidth: 1,
    padding: 14,
    borderRadius: 14,
    marginTop: 10,
  },
  errorTitle: { color: UI.dangerText, fontWeight: '900', fontSize: 16 },
  errorText: { color: UI.dangerText, marginTop: 6, lineHeight: 18 },

  emptyBox: {
    backgroundColor: UI.card,
    borderColor: UI.border,
    borderWidth: 1,
    padding: 14,
    borderRadius: 14,
    marginTop: 10,
  },
  emptyTitle: { color: UI.text, fontWeight: '900', fontSize: 16 },

  kpiGrid: { flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'space-between', marginBottom: 6 },
  kpiCard: {
    width: '48%',
    backgroundColor: UI.card,
    borderRadius: 16,
    padding: 12,
    marginBottom: 10,
    borderWidth: 1,
    borderColor: UI.border,
    ...padiproShadow,
    overflow: 'hidden',
  },
  kpiStripe: { position: 'absolute', left: 0, top: 0, bottom: 0, width: 5, opacity: 0.95 },
  kpiTopRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  kpiChip: { paddingVertical: 4, paddingHorizontal: 8, borderRadius: 999, borderWidth: 1 },
  kpiChipText: { color: UI.sub, fontWeight: '800', fontSize: 11 },
  kpiTitle: { color: UI.sub, fontWeight: '800', fontSize: 12 },
  kpiValue: { color: UI.text, fontWeight: '900', fontSize: 16, marginTop: 8 },
  kpiSub: { color: UI.sub, marginTop: 4, fontSize: 12, lineHeight: 16 },

  card: {
    backgroundColor: UI.card,
    borderRadius: 16,
    padding: 12,
    marginTop: 10,
    borderWidth: 1,
    borderColor: UI.border,
    ...padiproShadow,
  },
  cardHeadRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 2 },
  cardTitle: { color: UI.text, fontWeight: '900', fontSize: 16 },
  cardSub: { color: UI.sub, marginTop: 6, marginBottom: 10 },

  rowBetween: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 10,
    borderTopWidth: 1,
    borderTopColor: UI.grid,
  },
  rowKey: { color: UI.text, fontWeight: '800' },
  rowVal: { color: UI.text, fontWeight: '900' },
  varietyRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 11,
    borderTopWidth: 1,
    borderTopColor: UI.grid,
  },
  varietyFieldText: { color: PADIPRO.primaryDark, fontWeight: '800', fontSize: 11.5, marginTop: 4 },
  varietyDateText: { color: UI.faint, fontSize: 11, marginTop: 4 },
  hstBox: { minWidth: 112, alignItems: 'flex-end', padding: 9, borderRadius: 12, backgroundColor: PADIPRO.primarySoft },
  hstValue: { color: PADIPRO.primaryDark, fontWeight: '900', fontSize: 19 },
  hstLabel: { color: UI.sub, fontWeight: '700', fontSize: 10, marginTop: 1 },
  hstRange: { color: UI.text, fontWeight: '800', fontSize: 10.5, marginTop: 4 },

  breakRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 10,
    borderTopWidth: 1,
    borderTopColor: UI.grid,
  },
  barTrack: {
    height: 8,
    borderRadius: 999,
    backgroundColor: PADIPRO.surfaceSunken,
    marginTop: 8,
    overflow: 'hidden',
  },
  barFill: {
    height: 8,
    borderRadius: 999,
    backgroundColor: UI.bar,
    opacity: 0.35,
  },

  // Modal
  modalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(17,24,39,0.35)',
    padding: 16,
    justifyContent: 'center',
  },
  modalCard: {
    backgroundColor: UI.card,
    borderRadius: 18,
    borderWidth: 1,
    borderColor: UI.border,
    padding: 12,
    ...padiproShadow,
  },
  modalHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  modalTitle: { color: UI.text, fontWeight: '900', fontSize: 16 },
  modalCloseBtn: { paddingHorizontal: 10, paddingVertical: 8, borderRadius: 10, borderWidth: 1, borderColor: UI.border },
  modalCloseText: { color: UI.text, fontWeight: '900' },

  modalSearchWrap: { marginTop: 10, marginBottom: 10 },
  modalSearch: {
    borderWidth: 1,
    borderColor: UI.border,
    borderRadius: 12,
    paddingHorizontal: 12,
    paddingVertical: Platform.OS === 'ios' ? 12 : 10,
    color: UI.text,
    backgroundColor: PADIPRO.surfaceSunken,
  },
  modalSep: { height: 1, backgroundColor: UI.grid, marginLeft: 6, marginRight: 6 },
  modalRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: 12, paddingHorizontal: 6 },
  modalRowActive: { backgroundColor: PADIPRO.primarySoft, borderRadius: 12 },
  modalRowTitle: { color: UI.text, fontWeight: '900' },
  modalRowSub: { color: UI.sub, marginTop: 4, fontSize: 12 },

  badge: { borderWidth: 1, borderColor: UI.border, paddingVertical: 6, paddingHorizontal: 10, borderRadius: 999 },
  badgeText: { color: UI.text, fontWeight: '900', fontSize: 12 },
  badgeActive: { backgroundColor: PADIPRO.primary, paddingVertical: 6, paddingHorizontal: 10, borderRadius: 999 },
  badgeActiveText: { color: '#fff', fontWeight: '900', fontSize: 12 },
});
